/**
 * PRDetector — identifies Personal Records across standard distance categories.
 *
 * Pure service, no Firebase dependencies.
 */
export class PRDetector {
  /** Distance categories with inclusive km ranges. */
  static CATEGORIES = {
    '5K': { min: 4.5, max: 5.5 },
    '10K': { min: 9.5, max: 10.5 },
    'Half Marathon': { min: 20.5, max: 22 },
    'Marathon': { min: 41.5, max: 43 },
    'Ultra': { min: 43, max: Infinity },
  };

  /**
   * Detect PRs among the given races.
   *
   * - Only races with a time are considered.
   * - Mutates `race.isPR` on the input array (sets true for PR, false otherwise).
   * - Returns a Map of category name to the PR race entry
   *   ({ raceName, time, distance }).
   *
   * If two races in the same category have an identical time, the first one
   * encountered (by array position) wins.
   *
   * @param {RaceEntry[]} races
   * @returns {Map<string, { raceName: string, time: string, distance: number }>}
   */
  static detect(races) {
    const prMap = new Map(); // category → { raceName, time, distance, _index }

    if (!races || races.length === 0) return prMap;

    // Reset all isPR flags first
    races.forEach((r) => {
      r.isPR = false;
    });

    races.forEach((race, index) => {
      if (race.timeInSeconds == null) return; // skip races without times

      for (const [category, range] of Object.entries(PRDetector.CATEGORIES)) {
        if (race.distance >= range.min && race.distance < range.max) {
          const existing = prMap.get(category);
          if (!existing || race.timeInSeconds < existing._seconds) {
            prMap.set(category, {
              raceName: race.raceName,
              time: race.time,
              distance: race.distance,
              _seconds: race.timeInSeconds,
              _index: index,
            });
          }
          break; // a race belongs to at most one category
        }
      }
    });

    // Mark the winning races and clean up internal fields
    for (const [category, pr] of prMap) {
      races[pr._index].isPR = true;
      prMap.set(category, {
        raceName: pr.raceName,
        time: pr.time,
        distance: pr.distance,
      });
    }

    return prMap;
  }
}
